import { useState } from 'react';
import {
  CommandGroup,
  CommandEmpty,
} from '@/components/ui/command';
import { SearchableDropdown } from '@/components/ui/searchable-dropdown';
import { DropdownTrigger } from '@/react/components/ui/dropdown-trigger';
import { DropdownListItem } from '@/react/components/ui/dropdown-list-item';
import { SearchableDropdownCreateButton } from '@/react/components/ui/searchable-dropdown-create-button';
import { CloseIcon } from '@/react/components/icons/CloseIcon';

import { useSearchOrganisations, useCreateOrganisation } from '@hooks/organisations';
import { useDebouncedValue } from '@hooks/useDebouncedValue';
import { useTranslation } from '@hooks/useTranslation';
import { MIN_SEARCH_LENGTH } from '@/react/utils/searchQuery';
import { toast } from '@/hooks/use-toast';
import type { OrganisationSearchResult } from '@api/organisations';
import { HighlightMatch } from '@components/common/HighlightMatch';

const SEARCH_DEBOUNCE_MS = 300;

// Existing picks and freshly-created orgs both collapse to { id, name } — all
// the order payload (organisation_id) and the trigger label need.
export interface SelectedOrganisation {
  id: number;
  name: string;
}

interface CreateOrganisationComboboxProps {
  // Prefilled from the contact's primary org; null => individual order.
  selected: SelectedOrganisation | null;
  onSelect: (organisation: SelectedOrganisation) => void;
  onClear: () => void;
  disabled?: boolean;
}

// GRAIN-2485 / GRAIN-2021: the create-order org field. Replaces the free-text
// input that spawned typo-duplicates and rewired the contact's primary org.
//   * pick an existing org  -> emits its id (reuse kills typo-duplicates)
//   * "Create organisation" -> mints one real Organisation via the API from the
//                              typed name, then emits its id (order_type=Organisation)
//   * clear                 -> individual order; the backend leaves the
//                              contact's org links untouched
export function CreateOrganisationCombobox({
  selected,
  onSelect,
  onClear,
  disabled = false,
}: CreateOrganisationComboboxProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS);
  const trimmedQuery = debouncedSearchQuery.trim();

  const isSearching = trimmedQuery.length >= MIN_SEARCH_LENGTH;
  const { data: organisations, isLoading } = useSearchOrganisations(trimmedQuery);
  const createOrganisation = useCreateOrganisation();

  // The footer is always present and context-aware:
  //   * searching a name with no matches -> Create it (reuse-first: with
  //     results you pick from the list, so no create is offered);
  //   * otherwise, whenever an org is selected -> Clear back to an individual
  //     order (stays available even mid-search, not just when idle);
  //   * otherwise -> a disabled hint, so the footer never disappears.
  const showCreate = isSearching && !isLoading && (organisations?.length ?? 0) === 0;
  const showClear = !showCreate && selected != null;

  const handleOpenChange = (nextIsOpen: boolean) => {
    if (disabled && nextIsOpen) return;
    setIsOpen(nextIsOpen);
    if (!nextIsOpen) setSearchQuery('');
  };

  const handleSelect = (org: OrganisationSearchResult | SelectedOrganisation) => {
    onSelect({ id: org.id, name: org.name });
    handleOpenChange(false);
  };

  const handleClear = () => {
    onClear();
    // Reset the search but keep the dropdown open, so clearing returns the
    // field to a clean state ready to search/pick again without reopening.
    setSearchQuery('');
  };

  const handleCreate = async () => {
    try {
      const created = await createOrganisation.mutateAsync({ name: trimmedQuery });
      handleSelect(created);
    } catch {
      // Keep the dropdown open so the typed name isn't lost on a retry.
      toast({ description: t('create_order.errors.create_organisation_failed'), variant: 'destructive' });
    }
  };

  // Single footer slot (mirrors BillingAddressCombobox's `createButton`), whose
  // action swaps with context — create / clear / disabled hint. The top divider
  // only shows while searching (when the list has content above it); idle the
  // list is empty, so the divider would double up with the search bar's border.
  const footerAction = showClear ? (
    <SearchableDropdownCreateButton
      label={t('create_order.organisation_combobox.clear')}
      icon={CloseIcon}
      onSelect={handleClear}
      showSeparator={isSearching}
    />
  ) : (
    // Static "Create organisation" copy; disabled until a searched name has no
    // match (reuse-first). Minting still uses the typed query.
    <SearchableDropdownCreateButton
      label={t('create_order.organisation_combobox.create')}
      onSelect={handleCreate}
      disabled={!showCreate || createOrganisation.isPending}
      showSeparator={isSearching}
    />
  );

  return (
    <SearchableDropdown
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      selectedValue={selected ? String(selected.id) : undefined}
      searchPlaceholder={t('create_order.organisation_combobox.placeholder')}
      searchValue={searchQuery}
      onSearchChange={setSearchQuery}
      shouldFilter={false}
    >
      <SearchableDropdown.Trigger>
        <DropdownTrigger
          selectedLabel={selected?.name}
          placeholder={t('create_order.organisation_combobox.placeholder')}
          isOpen={isOpen}
          disabled={disabled}
        />
      </SearchableDropdown.Trigger>

      <SearchableDropdown.List showFadeGradient>
        {isSearching && isLoading && (
          <div className="p-4 text-center text-sm text-grey" role="status" aria-live="polite">
            {t('shared.loading')}
          </div>
        )}

        {/* Only the searched-but-empty case needs a list message; the idle
            "type to search" hint is dropped so the footer is the sole element
            (no stacked double border). */}
        {isSearching && !isLoading && (organisations?.length ?? 0) === 0 && (
          <CommandEmpty className="px-4 py-3 text-left text-sm text-grey">
            {t('create_order.organisation_combobox.no_results')}
          </CommandEmpty>
        )}

        {isSearching && organisations && organisations.length > 0 && (
          <CommandGroup className="overflow-visible p-0">
            <div className="flex flex-col gap-2 py-2">
              {organisations.map((org) => (
                <DropdownListItem
                  key={org.id}
                  value={String(org.id)}
                  onSelect={() => handleSelect(org)}
                  primary={<HighlightMatch text={org.name} query={trimmedQuery} />}
                  primaryTitle={org.name}
                />
              ))}
            </div>
          </CommandGroup>
        )}
      </SearchableDropdown.List>

      <SearchableDropdown.Footer>
        {footerAction}
      </SearchableDropdown.Footer>
    </SearchableDropdown>
  );
}
